import {Game} from "../Game";
import {ISimplePresentation, ISimplePresentationRunnerController} from "../SimplePresentationRunner";
import {IMonster, IMonsterWalk, MONSTER_EXPRESSION, MONSTER_WEAPON_ACTIONS} from "../server/IData";
import {gsap} from "gsap";
import {MonsterSymbol} from "../reels/MonsterSymbol";
import { Sound } from "../sound/Sound";
import { MathHelper } from "@nolimitcity/slot-game/bin/utils/MathHelper";

export class SuicideBombRunner implements ISimplePresentation {

    private _sbItems: MonsterSymbol[] = [];

    public shouldRun(): boolean {
        return Game.model.gameData.suicideBombs && Game.model.gameData.suicideBombs.length > 0 && !Game.model.gameData.suspendedAction;
    }

    public start(controller: ISimplePresentationRunnerController): void {
        const tl: gsap.core.Timeline = gsap.timeline({
                onComplete: () => {
                    this._sbItems = [];

                    controller.finish();
                }
            }),
            monsters: IMonster[] = Game.model.gameData.monsters,
            suicideBombs: IMonsterWalk[] = Game.model.gameData.suicideBombs,
            prevActiveReels: number = Game.ACTIVE_REELS_NUM,
            prevActiveRows: number = Game.ACTIVE_ROWS_NUM;

        tl.add(() => {
            controller.setSkipCallback(() => {
                this.onSkip(tl);
            });
        });

        // #254
        this._sbItems = [];
        suicideBombs.forEach((sbItem: IMonsterWalk) => {
            for (let i: number = 0; i < monsters.length; i++) {
                const monster: IMonster = monsters[i];
                if (monster && monster.path && monster.path.length && sbItem.x === monster.path[0].x && sbItem.y === monster.path[0].y) {
                    this._sbItems.push(Game.reelsView.reelModels[sbItem.x].getMonsterSymbolAt(monster.symbol));

                    break;
                }
            }
        });

        const expansionDirection: number[] = Game.model.gameData.expansionDirections[0];
        const isSuicideBig: boolean = suicideBombs.length === 4,
            maxExpansion: boolean = Game.model.gameData.maxExpansion;

        tl.addLabel("doExplosion", 1.4); // declaring it here as this label is needed earlier

        // #337
        if (!isSuicideBig) {
            const suicideBomb: IMonsterWalk = suicideBombs[0],
                targetRowId: number = suicideBomb.y,
                monstersToExplode: string[] = [];

            Game.getMonstersToExplode(suicideBomb.x - 1, targetRowId - 2, targetRowId + 3, monstersToExplode);
            Game.getMonstersToExplode(suicideBomb.x, targetRowId - 2, targetRowId + 3, monstersToExplode);
            Game.getMonstersToExplode(suicideBomb.x + 1, targetRowId - 2, targetRowId + 3, monstersToExplode);
            Game.getMonstersToExplode(suicideBomb.x - 2, targetRowId - 1, targetRowId + 2, monstersToExplode);
            Game.getMonstersToExplode(suicideBomb.x + 2, targetRowId - 1, targetRowId + 2, monstersToExplode);

            Game.monsterReelsView.monsterSymbolContainer.children.forEach((monster: PIXI.DisplayObject) => {
                if (monstersToExplode.includes(monster.name)) {
                    tl.add((monster as MonsterSymbol).getStateAnimation(MONSTER_EXPRESSION.SHOCK, 5), 0);
                } else {
                    tl.add([
                        (monster as MonsterSymbol).getStateAnimation(MONSTER_EXPRESSION.LAUGH, 5),
                        () => {
                            Game.soundPlayer.playSound(monster.name + "Laugh" + MathHelper.randomIntInRange(1, 3), 1);
                        }
                    ], "doExplosion");
                }
            });
        }

        // monster suicide animation
        suicideBombs.forEach((sbItem: IMonsterWalk, idx: number) => {
            const monsterSymbol: MonsterSymbol = this._sbItems[idx];

            if (isSuicideBig) {
                if (Game.reelCorners.getLife(Game.MONSTER_NAME_TO_IDX[monsterSymbol.name])===1){
                    tl.add(monsterSymbol.getStateAnimation(MONSTER_WEAPON_ACTIONS.SUICIDE_BIG_OOL, 5), 0);
                } else{
                    tl.add(monsterSymbol.getStateAnimation(MONSTER_WEAPON_ACTIONS.SUICIDE_BIG, 5), 0);
                }
            } else {
                tl.add(monsterSymbol.getStateAnimation(MONSTER_WEAPON_ACTIONS.SUICIDE, 5), 0);
            }
        });

        // all explode
        if (isSuicideBig) {
            for (let reelId: number = 0; reelId < Game.ACTIVE_REELS_NUM; reelId++) {
                Game.updateSurprisedMonstersInExplosion(reelId, 0, Game.ACTIVE_ROWS_NUM);
                tl.add(Game.explodeSymbols(reelId, 0, Game.ACTIVE_ROWS_NUM), "doExplosion");
                for (let rowId: number = 0; rowId < Game.ACTIVE_ROWS_NUM; rowId++){
                    if ((rowId === 0 || rowId === Game.ACTIVE_ROWS_NUM-1) && (reelId===0 || reelId===Game.ACTIVE_REELS_NUM-1)){
                        continue;
                    }
                    tl.add(Game.symbolFloors.playExplodePatternHighlightAnimation(reelId, rowId), 0);
                }
            }
            tl.add(() => {
                Game.soundPlayer.playSound(Sound.SUICIDE_BOMB_BIG, 1);
            }, 0);

            if (!maxExpansion && expansionDirection) {
                const extraReels: number = (expansionDirection[0] === 0 ? 0 : (Game.ACTIVE_REELS_NUM === 8 ? 1 : 2)),
                    extraRows: number = (expansionDirection[1] === 0 ? 0 : (Game.ACTIVE_ROWS_NUM === 8 ? 1 : 2)),
                    addReelsToRight: boolean = (extraReels > 0 && expansionDirection[0] === 1),
                    addRowsInBottom: boolean = (extraRows > 0 && expansionDirection[1] === 1);

                const {
                    leftCRReelId,
                    leftCRRowId,
                    rightCRReelId,
                    rightCRRowId
                } = Game.cornerRemovalUtils(extraReels, extraRows, addReelsToRight, addRowsInBottom);
                tl.add([
                    Game.handleCornerRemoval(leftCRReelId, leftCRRowId, rightCRReelId, rightCRRowId),
                    Game.expandGrid(extraReels, extraRows, addReelsToRight, addRowsInBottom),
                ], "doExplosion");

                tl.add(tl.add(this.showSuicideExplodePatternAfterExpansion(addReelsToRight, addRowsInBottom, prevActiveReels, prevActiveRows), "doExplosion"));
            } else if (maxExpansion){
                tl.add(Game.explodeSymbolsBeforeMaxExpansion(), "<0.5");
                tl.add(Game.expandGridForMaxExpansion(), "<0.55");
            }
        } else {
            tl.add(() => {
                Game.soundPlayer.playSound(Sound.SUICIDE_BOMB, 1);
            }, 0);

            suicideBombs.forEach((sbItem: IMonsterWalk, idx: number) => {
                const sBSymbol: MonsterSymbol = this._sbItems[idx];

                tl.add(this.showSuicideExplosionHighlight(sbItem), 0);
                tl.add(this.handleExplosions(sbItem, sBSymbol), "doExplosion");
            });
        }

        tl.add(()=>{
            Game.shakeScreen()
        }, "-=0.8");
    }

    private handleExplosions(suicideBomb: IMonsterWalk, sBSymbol: MonsterSymbol): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            targetRowId: number = suicideBomb.y;

        Game.updateSurprisedMonstersInExplosion(suicideBomb.x - 1, targetRowId - 2, targetRowId + 3),
        Game.updateSurprisedMonstersInExplosion(suicideBomb.x, targetRowId - 2, targetRowId + 3),
        Game.updateSurprisedMonstersInExplosion(suicideBomb.x + 1, targetRowId - 2, targetRowId + 3),
        Game.updateSurprisedMonstersInExplosion(suicideBomb.x - 2, targetRowId - 1, targetRowId + 2),
        Game.updateSurprisedMonstersInExplosion(suicideBomb.x + 2, targetRowId - 1, targetRowId + 2),
            
        tl.add([
            Game.explodeSymbols(suicideBomb.x - 1, targetRowId - 2, targetRowId + 3),
            Game.explodeSymbols(suicideBomb.x, targetRowId - 2, targetRowId + 3),
            Game.explodeSymbols(suicideBomb.x + 1, targetRowId - 2, targetRowId + 3),
            Game.explodeSymbols(suicideBomb.x - 2, targetRowId - 1, targetRowId + 2),
            Game.explodeSymbols(suicideBomb.x + 2, targetRowId - 1, targetRowId + 2),
        ]);

        tl.add(() => {
            sBSymbol.isRemoved = true;
        });

        return tl;
    }

    private showSuicideExplosionHighlight(suicideBomb: IMonsterWalk): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        
        for (let i=0; i<Game.ACTIVE_REELS_NUM; i++){
            for (let j=0; j<Game.ACTIVE_ROWS_NUM; j++){
                if ((i===suicideBomb.x-2 && j===suicideBomb.y-2) || (i===suicideBomb.x-2 && j===suicideBomb.y+2) || (i===suicideBomb.x+2 && j===suicideBomb.y-2) || (i===suicideBomb.x+2 && j===suicideBomb.y+2) || (i<suicideBomb.x-2 || i>suicideBomb.x+2 || j<suicideBomb.y-2 || j>suicideBomb.y+2)){
                    continue;
                }

                tl.add(Game.symbolFloors.playExplodePatternHighlightAnimation(i, j), 0);
            }
        }
        return tl;
    }

    private showSuicideExplodePatternAfterExpansion(addReelsToRight: boolean, addRowsInBottom: boolean, prevReelsNum: number, prevRowsNum: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        
        tl.add(() => {
            Game.soundPlayer.playSound(Sound.ATTACK_HIGHLIGHT, 1)
        });

        if (!(prevReelsNum === Game.TOTAL_REELS_NUM && prevRowsNum === Game.TOTAL_REELS_NUM)){
            if (Game.ACTIVE_REELS_NUM > prevReelsNum){
                if (addReelsToRight){
                    for (let i=prevReelsNum; i<Game.ACTIVE_REELS_NUM; i++){
                        for (let j=0; j<Game.ACTIVE_ROWS_NUM; j++){
                            // two less explode for first expanded reel
                            // if (i===prevReelsNum && (j===0 || j==Game.ACTIVE_ROWS_NUM-1)){
                            //     continue;
                            // }

                            tl.add([
                                Game.showExplodePatternHighlighterOnSymbols(i, j, j+1),
                                Game.showExplodePatternSparkOnSymbols(i, j, j+1)
                            ], 0);
                        }
                    }
                } else{
                    for (let i=0; i<Game.ACTIVE_REELS_NUM-prevReelsNum; i++){
                        for (let j=0; j<Game.ACTIVE_ROWS_NUM; j++){
                            // two less explode for first expanded reel
                            // if (i===Game.ACTIVE_REELS_NUM-prevReelsNum-1 && (j===0 || j==Game.ACTIVE_ROWS_NUM-1)){
                            //     continue;
                            // }

                            tl.add([
                                Game.showExplodePatternHighlighterOnSymbols(i, j, j+1),
                                Game.showExplodePatternSparkOnSymbols(i, j, j+1)
                            ], 0);
                        }
                    }
                }
            } else if (Game.ACTIVE_ROWS_NUM > prevRowsNum){
                if (addRowsInBottom){
                    for (let i=prevRowsNum; i<Game.ACTIVE_ROWS_NUM; i++){
                        for (let j=0; j<Game.ACTIVE_REELS_NUM; j++){
                            // two less explode for first expanded row
                            // if (i===prevRowsNum && (j===0 || j==Game.ACTIVE_REELS_NUM-1)){
                            //     continue;
                            // }

                            tl.add([
                                Game.showExplodePatternHighlighterOnSymbols(j, i, i+1),
                                Game.showExplodePatternSparkOnSymbols(j, i, i+1)
                            ], 0);
                        }
                    }
                } else{
                    for (let i=-1; i<Game.ACTIVE_ROWS_NUM-prevRowsNum; i++){
                        for (let j=0; j<Game.ACTIVE_REELS_NUM; j++){
                            // two less explode for first expanded row
                            // if (i===(Game.ACTIVE_ROWS_NUM-prevRowsNum-1) && (j===0 || j==Game.ACTIVE_REELS_NUM-1)){
                            //     continue;
                            // }
                        
                            tl.add([
                                Game.showExplodePatternHighlighterOnSymbols(j, i, i+1),
                                Game.showExplodePatternSparkOnSymbols(j, i, i+1)
                            ], 0);
                        }
                    }
                }
            }
        }

        return tl;
    }


    private onSkip(tl: gsap.core.Timeline): void {
        tl.totalProgress(1);
        Game.soundPlayer.stopSound(Sound.SUICIDE_BOMB);
        Game.soundPlayer.stopSound(Sound.SUICIDE_BOMB_BIG);
    }
}